<template>
  <b-container
    class="view-edit-panel"
    fluid
  >
    <b-row v-if="showHeader">
      <b-col cols="12" class="p-0">
        <h2>
          <slot name="header">
            <label class="view-edit-panel__title">
              <slot name="title">
                {{ title }}
              </slot>
            </label>
          </slot>
        </h2>
      </b-col>
    </b-row>
    <b-row>
      <slot
        :editing="editing"
        :item="valueToHandle"
        :saving="saving"
      />
    </b-row>
    <b-row
      v-if="!loading"
      class="view-edit-panel__btns no-internal-print"
      align-h="end"
    >
      <b-col
        v-if="!editing"
        class="mt-3"
        sm="auto"
        xs="12"
      >
        <b-button
          v-if="canEdit"
          v-show="!hideEdit"
          class="btn-block"
          variant="primary"
          @click="edit"
        >
          {{ $t('actions.edit') }}
        </b-button>
      </b-col>
      <template v-else>
        <b-col
          class="mt-3"
          sm="auto"
          xs="12"
        >
          <b-button
            class="btn-block"
            @click="cancel"
            :disabled="!canCancel"
          >
            {{ $t('actions.cancel') }}
          </b-button>
        </b-col>
        <b-col
          class="mt-3"
          sm="auto"
          xs="12"
        >
          <s-loading-button
            class="btn-block"
            :disabled="disableSave"
            @click="$event.wait(confirmSaving())"
          >
            {{ $t('actions.save') }}
          </s-loading-button>
        </b-col>
      </template>
    </b-row>
  </b-container>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep';

/**
 * Container de edição/visualização de dados.
 *
 * Este componente funciona como um container onde os dados internos alternam entre modo de edição
 * e visualização.
 *
 * Os botões do rodapé, Editar, Cancelar e Salvar, fazem o controle do modo de edição, bem como
 * mensagens de confirmação e sucesso.
 *
 * Os dados que serão editados deverão ser passados por v-model. Internamente, este container
 * controla a edição e desfaz alterações se houver um cancelamento. Para o controle automatizado
 * de alterações e cancelamentos, os binds dos campos internos devem ser feitos para o escopo
 * do slot, e não para o objeto diretamente. Em resumo, usando v-model e slot-scope, um cancelamento
 * faz com que os dados sejam revertidos automaticamente.
 *
 * Exemplo:
 *
 * ```html
 *  <view-edit-panel v-model="meusDados">
 *    <div slot-scope="dadosEmEdicao">
 *      Bind correto:
 *      <input v-model="dadosEmEdicao.item.campo1" />
 *      Bind incorreto:
 *      <input v-model="meusDados.campo1" />
 *    </div>
 *  </view-edit-panel>
 * ```
 *
 * O Evento changing (citado abaixo) é obrigatório, pois é a partir dele que o ciclo de edição se
 * encerra.
 *
 * Slots:
 * - padrão: é o corpo do componente, com os campos.
 *     Recebe por escopo as propriedades:
 *      - "item": dados em associação, passados por v-model.
 *      - "editing": booleano que indica se está em modo de edição ou visualização.
 *      - "saving": booleano que indica se está acontecendo um processo de salvamento.
 * - header: troca o cabaçalho inteiro;
 * - title: texto do cabeçalho, dentro do label já estilizado no padrão do componente.
 *
 * Eventos:
 * - changing: disparado quando o usuário clica no botão salvar e confirma a mensagem exbida na
 *     tela.
 *     A ideia desse evento é que a partir dele seja disparada a requisição ao serviço que vai
 *     salvar os dados e após o salvamento deve-se confirmar via accept ou reject, citados abaixo.
 *     Argumentos do evento: Objeto { newValue: any, accept: function, reject: function }.
 *     - newValue: carrega um clone do valor passado por v-model e que foi editado na tela. Somente
 *       leitura.
 *     - accept: deve ser chamado para aceitar as alterações, que serão propagadas para v-model
 *       via evento input, e finalizando o modo de edição.
 *     - reject: deve ser chamado para sinalizar que não foi possível salvar, fazendo com que o
 *       container continue em modo de edição.
 * - input: sinaliza que o valor foi alterado para compatibilidade com v-model. É emitido apenas
 *     quando um evento "changing" tem o seu "accept" invocado.
 * - edit: Quando é clicado no botão de edição, iniciando o modo de edição. Recebe como argumento o
 *     valor/referência que foi clonado para edição. Essa referência será mantida até que uma nova
 *     edição se inicie. Caso precise interagir com os dados em edição via código, use essa
 *     referência. Quando a edição acaba (evento input ou cancel), essa referência não é mais
 *     utilizada.
 * - cancel: quando é clicado no botão cancelar e confirmado via mensagem.
 */
export default {
  name: 'ViewEditPanel',
  props: {
    /**
     * Objeto com dados. Compatível com v-model, onde o input é disparado na confirmação
     * do salvar através do evento `changing`.
     */
    value: null,
    /**
     *  Enquanto está sendo carregado o conteúdo esconde as ações
     */
    loading: {
      type: [Boolean, Number],
      default: false,
    },
    /**
     * Título do componente. Também há o slot `title`.
     */
    title: {
      type: String,
      required: false,
    },
    /**
     * Permissão necessária para habilitar o botão de edição.
     */
    permissionToEdit: {
      type: String,
      default: 'PER001',
    },
    /**
     * Permite desabilitar forçosamente o botão de edição.
     */
    disableEditing: {
      type: Boolean,
      default: false,
    },
    /**
     * Ocultar o botão de edição caso usuário não possua permissão.
     */
    hideEditWhenWithoutPermission: {
      type: Boolean,
      default: false,
    },
    /**
     * Permite desabilitar forçosamente o botão de salvamento.
     */
    disableSave: {
      type: Boolean,
      default: false,
    },
    /**
     * Permite desabilitar forçosamente o botão de cancelamento.
     */
    disableCancel: {
      type: Boolean,
      default: false,
    },
    /**
     * Código da mensagem de confirmação ao salvar.
     */
    savingConfirmCode: {
      type: [String, Array],
      default: 'MC011',
    },
    /**
     * Código da mensagem de sucesso ao salvar.
     */
    savingSuccessCode: {
      type: String,
      default: 'MS006',
    },
  },
  data: () => ({
    editingValue: null,
    saving: false,
  }),
  computed: {
    editing: {
      get() {
        return this.editingValue != null;
      },
      set(value) {
        if (value) {
          this.editingValue = cloneDeep(this.value);
          return;
        }

        this.editingValue = null;
      },
    },
    valueToHandle() {
      return this.editing ? this.editingValue : this.value;
    },
    showHeader() {
      return this.title || this.$slots.title || this.$slots.header;
    },
    hideEdit() {
      return this.hideEditWhenWithoutPermission && !this.$hasUserPermission(this.permissionToEdit);
    },
    canEdit() {
      return !this.disableEditing && this.$hasUserPermission(this.permissionToEdit);
    },
    canCancel() {
      return !this.saving && !this.disableCancel;
    },
  },
  methods: {
    edit() {
      this.editing = true;
      this.$emit('edit', this.editingValue);
    },
    save(valueToSave) {
      this.$emit('input', valueToSave);
      this.editing = false;
      this.$pushAlert({ code: this.savingSuccessCode });
    },
    async confirmSaving() {
      const valueToSave = cloneDeep(this.editingValue);
      this.$emit('init-saving', valueToSave);
      await this.$nextTick();
      try {
        this.saving = true;

        if (Array.isArray(this.savingConfirmCode)) {
          const allNotificationsHaveBeenConfirmed = await this.createChainMessage();
          if (!allNotificationsHaveBeenConfirmed) return;
          if (await this.fireChangingValueEvent(valueToSave)) {
            this.save(valueToSave);
          }
          return;
        }

        await this.confirmSavingSingleMessage(valueToSave);
      } finally {
        this.saving = false;
      }
    },
    async confirmSavingSingleMessage(valueToSave) {
      if (await this.$confirmMessage({ code: this.savingConfirmCode }).toBool()
        && await this.fireChangingValueEvent(valueToSave)) {
          this.save(valueToSave);
      }
    },
    async createChainMessage() {
      return this.savingConfirmCode
        .reduce((chain, messageCode) => chain.then((previousOptionSelected) => {
          const previousOptionSelectedIsNo = previousOptionSelected === false;
          if (previousOptionSelectedIsNo) return null;
          return this.$confirmMessage({ code: messageCode }).toBool();
        }), Promise.resolve());
    },
    cancel() {
      this.$confirmMessage({
        code: 'MC006',
      }).accepted(() => {
        this.editing = false;
        this.$emit('cancel');
      });
    },
    fireChangingValueEvent(newValue) {
      return new Promise((resolve) => {
        this.$emit('changing', {
          newValue,
          accept: () => resolve(true),
          reject: () => resolve(false),
        });
      });
    },
  },
};
</script>

<style lang="sass" scoped>
</style>
